import React, {createContext, useContext} from 'react';

import {
    NoticeData,
    PersonalPlanLandingBannerNoticeData,
    usePersonalPlanLandingBannerNotice,
} from '@udemy/notices';
import {SubscriptionCTAProps} from '@udemy/subscription-browse';
import {useConsumerSubscriptionPlan} from '@udemy/subscription-graphql';

import {
    NON_CATALOG_COURSE_QUERY_PARAM,
    useNonCatalogCourseParam,
} from 'src/hooks/useNonCatalogCourseParam';
import {useSubscriptionCheckoutEntryParam} from 'src/hooks/useSubscriptionCheckoutEntryParam';

import {useSuppressPersonalPlanCampaignNotice} from './hooks/useSuppressPersonalPlanCampaignNotice';

const PersonalPlanSubscriptionCheckoutEntryContext = createContext<string | undefined>(undefined);

/**
 * Supplies `subscription_checkout_entry` from the LP URL to nested checkout CTAs.
 */
export function PersonalPlanSubscriptionCheckoutEntryProvider({
    children,
}: {
    children: React.ReactNode;
}): React.ReactElement {
    const subscriptionCheckoutEntry = useSubscriptionCheckoutEntryParam();
    return (
        <PersonalPlanSubscriptionCheckoutEntryContext.Provider value={subscriptionCheckoutEntry}>
            {children}
        </PersonalPlanSubscriptionCheckoutEntryContext.Provider>
    );
}

export function usePersonalPlanSubscriptionCheckoutEntry(): string | undefined {
    return useContext(PersonalPlanSubscriptionCheckoutEntryContext);
}

type CampaignNoticeContextValue = NoticeData<PersonalPlanLandingBannerNoticeData> | undefined;

const PersonalPlanCampaignNoticeContext = createContext<CampaignNoticeContextValue>(undefined);

export function PersonalPlanCampaignNoticeProvider({
    children,
}: {
    children: React.ReactNode;
}): React.ReactElement {
    const notice = usePersonalPlanLandingBannerNotice();
    return (
        <PersonalPlanCampaignNoticeContext.Provider value={notice}>
            {children}
        </PersonalPlanCampaignNoticeContext.Provider>
    );
}

export function usePersonalPlanCampaignNotice(): CampaignNoticeContextValue {
    return useContext(PersonalPlanCampaignNoticeContext);
}

export function usePersonalPlanCampaignActive(): boolean {
    const notice = usePersonalPlanCampaignNotice();
    const plan = useConsumerSubscriptionPlan();
    const suppressCampaignNotice = useSuppressPersonalPlanCampaignNotice(plan);

    if (suppressCampaignNotice || !notice || notice.isLoading) {
        return false;
    }
    return notice.notices.length > 0 && Boolean(notice.notices[0]?.data?.cta_1_url);
}

/**
 * Returns `true` while the campaign notice is still in flight. So
 * we don't render a clickable link that points to the wrong URL before the notice
 * resolves.
 */
export function usePersonalPlanCampaignNoticeLoading(): boolean {
    const notice = usePersonalPlanCampaignNotice();
    return !notice || Boolean(notice.isLoading);
}

/**
 * Source of truth for what every PPLP subscribe CTA should pass to
 * `SubscriptionCTA`
 */
export function useNonCatalogCheckoutSource(): {
    source: SubscriptionCTAProps['source'];
    noticeDataLoading: boolean;
} {
    const nonCatalogCourseId = useNonCatalogCourseParam();
    const isCampaignActive = usePersonalPlanCampaignActive();
    const isCampaignLoading = usePersonalPlanCampaignNoticeLoading();
    return {
        source:
            !isCampaignActive && nonCatalogCourseId
                ? {type: NON_CATALOG_COURSE_QUERY_PARAM, id: nonCatalogCourseId}
                : undefined,
        noticeDataLoading: !!nonCatalogCourseId && isCampaignLoading,
    };
}
